import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Database, Webhook, Activity, ShieldCheck, Layers, Sparkles, BarChart3,
  Plus, Trash2, RefreshCw, Play, Copy, CheckCircle2, XCircle, Clock,
  ArrowRight, Settings, Search, Download, Upload, Code, Eye, Edit
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DataHubPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  
  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  
  // Webhooks state
  const [webhooks, setWebhooks] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState({});
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [webhookForm, setWebhookForm] = useState({
    name: '',
    description: '',
    endpoint_url: '',
    events: [],
    is_active: true
  });
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  
  // Events state
  const [events, setEvents] = useState([]);
  const [eventStats, setEventStats] = useState(null);
  
  // Data Quality state
  const [qualityRules, setQualityRules] = useState([]);
  const [qualityIssues, setQualityIssues] = useState([]);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [ruleForm, setRuleForm] = useState({
    name: '',
    entity_type: 'contact',
    field_name: '',
    rule_type: 'required',
    severity: 'warning',
    is_active: true
  });
  
  // Data Models state
  const [dataModels, setDataModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  
  // Enrichment state
  const [enrichmentProviders, setEnrichmentProviders] = useState([]);
  const [enrichmentJobs, setEnrichmentJobs] = useState([]);
  
  // Data Studio state
  const [queryConfig, setQueryConfig] = useState({
    collection: 'contacts',
    filters: {},
    fields: [],
    limit: 100
  });
  const [queryResults, setQueryResults] = useState(null);
  const [collections, setCollections] = useState([]);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/data-hub/dashboard`, { headers });
      setDashboard(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  }, []);

  const fetchWebhooks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/data-hub/webhooks`, { headers });
      setWebhooks(res.data);
    } catch (err) {
      console.error('Failed to fetch webhooks:', err);
    }
  }, []);

  const fetchWebhookEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/data-hub/webhooks/events`, { headers });
      setWebhookEvents(res.data);
    } catch (err) {
      console.error('Failed to fetch webhook events:', err);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/data-hub/events?limit=50`, { headers });
      setEvents(res.data);
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  }, []);

  const fetchEventStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/data-hub/events/stats`, { headers });
      setEventStats(res.data);
    } catch (err) {
      console.error('Failed to fetch event stats:', err);
    }
  }, []);

  const fetchQualityRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/data-hub/quality/rules`, { headers });
      setQualityRules(res.data);
    } catch (err) {
      console.error('Failed to fetch quality rules:', err);
    }
  }, []);

  const fetchQualityIssues = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/data-hub/quality/issues?status=open`, { headers });
      setQualityIssues(res.data);
    } catch (err) {
      console.error('Failed to fetch quality issues:', err);
    }
  }, []);

  const fetchDataModels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/data-hub/models`, { headers });
      setDataModels(res.data);
    } catch (err) {
      console.error('Failed to fetch data models:', err);
    }
  }, []);

  const fetchEnrichmentProviders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/data-hub/enrichment/providers`, { headers });
      setEnrichmentProviders(res.data);
    } catch (err) {
      console.error('Failed to fetch enrichment providers:', err);
    }
  }, []);

  const fetchCollections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/data-hub/studio/collections`, { headers });
      setCollections(res.data.collections);
    } catch (err) {
      console.error('Failed to fetch collections:', err);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchWebhooks();
    fetchWebhookEvents();
    fetchEvents();
    fetchEventStats();
    fetchQualityRules();
    fetchQualityIssues();
    fetchDataModels();
    fetchEnrichmentProviders();
    fetchCollections();
  }, []);

  // Webhook functions
  const createWebhook = async () => {
    if (!webhookForm.name || !webhookForm.endpoint_url) {
      toast.error('Name and endpoint URL are required');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/data-hub/webhooks`, webhookForm, { headers });
      fetchWebhooks();
      setWebhookModalOpen(false);
      setWebhookForm({ name: '', description: '', endpoint_url: '', events: [], is_active: true });
      toast.success('Webhook created');
    } catch (err) {
      toast.error('Failed to create webhook');
    } finally {
      setLoading(false);
    }
  };

  const deleteWebhook = async (id) => {
    if (!confirm('Delete this webhook?')) return;
    try {
      await axios.delete(`${API}/api/data-hub/webhooks/${id}`, { headers });
      fetchWebhooks();
      toast.success('Webhook deleted');
    } catch (err) {
      toast.error('Failed to delete webhook');
    }
  };

  const testWebhook = async (id) => {
    try {
      await axios.post(`${API}/api/data-hub/webhooks/${id}/test`, {}, { headers });
      toast.success('Test webhook sent');
    } catch (err) {
      toast.error('Failed to test webhook');
    }
  };

  const viewWebhookLogs = async (webhook) => {
    setSelectedWebhook(webhook);
    try {
      const res = await axios.get(`${API}/api/data-hub/webhooks/${webhook.id}/logs`, { headers });
      setWebhookLogs(res.data);
      setLogsModalOpen(true);
    } catch (err) {
      toast.error('Failed to load logs');
    }
  };

  // Quality rule functions
  const createQualityRule = async () => {
    if (!ruleForm.name || !ruleForm.field_name) {
      toast.error('Name and field are required');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/data-hub/quality/rules`, ruleForm, { headers });
      fetchQualityRules();
      setRuleModalOpen(false);
      setRuleForm({ name: '', entity_type: 'contact', field_name: '', rule_type: 'required', severity: 'warning', is_active: true });
      toast.success('Rule created');
    } catch (err) {
      toast.error('Failed to create rule');
    } finally {
      setLoading(false);
    }
  };

  const deleteQualityRule = async (id) => {
    if (!confirm('Delete this rule?')) return;
    try {
      await axios.delete(`${API}/api/data-hub/quality/rules/${id}`, { headers });
      fetchQualityRules();
      toast.success('Rule deleted');
    } catch (err) {
      toast.error('Failed to delete rule');
    }
  };

  const resolveIssue = async (id) => {
    try {
      await axios.put(`${API}/api/data-hub/quality/issues/${id}/resolve`, {}, { headers });
      fetchQualityIssues();
      toast.success('Issue resolved');
    } catch (err) {
      toast.error('Failed to resolve issue');
    }
  };

  // Data Studio functions
  const runQuery = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/data-hub/studio/query`, queryConfig, { headers });
      setQueryResults(res.data);
    } catch (err) {
      toast.error('Failed to run query');
    } finally {
      setLoading(false);
    }
  };

  const exportResults = async () => {
    if (!queryResults) return;
    const blob = new Blob([JSON.stringify(queryResults.results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `export_${queryConfig.collection}_${Date.now()}.json`;
    a.click();
    toast.success('Exported to JSON');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const toggleEventSelection = (event) => {
    const newEvents = webhookForm.events.includes(event)
      ? webhookForm.events.filter(e => e !== event)
      : [...webhookForm.events, event];
    setWebhookForm({ ...webhookForm, events: newEvents });
  };

  return (
    <div className="space-y-6" data-testid="data-hub-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Database className="h-6 w-6" />
            Data Hub
          </h1>
          <p className="text-muted-foreground">Manage data integrations, quality, and enrichment</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-4xl">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
          <TabsTrigger value="quality" data-testid="tab-quality">Data Quality</TabsTrigger>
          <TabsTrigger value="models" data-testid="tab-models">Models</TabsTrigger>
          <TabsTrigger value="studio" data-testid="tab-studio">Data Studio</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {dashboard && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Webhook className="h-4 w-4" />
                      Webhooks
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dashboard.webhooks?.active || 0}</div>
                    <p className="text-xs text-muted-foreground">
                      {dashboard.webhooks?.calls_24h || 0} calls today • {dashboard.webhooks?.success_rate || 100}% success
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Events (24h)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dashboard.events?.last_24h || 0}</div>
                    <p className="text-xs text-muted-foreground">
                      {dashboard.events?.last_7d || 0} events this week
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4" />
                      Data Quality
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dashboard.data_quality?.active_rules || 0}</div>
                    <p className="text-xs text-muted-foreground">
                      {dashboard.data_quality?.open_issues || 0} open issues
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Enrichment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dashboard.enrichment?.jobs_24h || 0}</div>
                    <p className="text-xs text-muted-foreground">
                      {dashboard.enrichment?.success_rate || 100}% success rate
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Events */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {dashboard.recent_events?.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No recent events</p>
                    ) : (
                      dashboard.recent_events?.slice(0, 5).map((event, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline">{event.source}</Badge>
                            <div>
                              <p className="font-medium text-sm">{event.event_type}</p>
                              <p className="text-xs text-muted-foreground">{event.entity_type} • {event.action}</p>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">{formatDate(event.created_at)}</p>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Webhooks</CardTitle>
                <CardDescription>Send data to external services when events occur</CardDescription>
              </div>
              <Button onClick={() => setWebhookModalOpen(true)} data-testid="create-webhook-btn">
                <Plus className="h-4 w-4 mr-1" />
                Create Webhook
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {webhooks.length === 0 ? (
                  <div className="text-center py-8">
                    <Webhook className="h-12 w-12 mx-auto text-slate-200 mb-2" />
                    <p className="text-muted-foreground">No webhooks configured</p>
                  </div>
                ) : (
                  webhooks.map((webhook) => (
                    <div key={webhook.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`p-2 rounded-lg ${webhook.is_active ? 'bg-green-100' : 'bg-slate-100'}`}>
                          <Webhook className={`h-5 w-5 ${webhook.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{webhook.name}</p>
                            {webhook.is_active ? (
                              <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Inactive</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate max-w-md">{webhook.endpoint_url}</p>
                          <p className="text-xs text-muted-foreground">
                            {webhook.events?.length || 0} events • {webhook.trigger_count || 0} triggers
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => viewWebhookLogs(webhook)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => testWebhook(webhook.id)}>
                          <Play className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteWebhook(webhook.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Events by Source */}
            {eventStats && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Events by Source (24h)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(eventStats.by_source || {}).map(([source, count]) => (
                      <div key={source} className="flex items-center justify-between">
                        <Badge variant="outline">{source}</Badge>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                    {Object.keys(eventStats.by_source || {}).length === 0 && (
                      <p className="text-muted-foreground text-sm">No events in last 24h</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Events by Action */}
            {eventStats && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Events by Action (24h)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(eventStats.by_action || {}).map(([action, count]) => (
                      <div key={action} className="flex items-center justify-between">
                        <span className="text-sm">{action}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                    {Object.keys(eventStats.by_action || {}).length === 0 && (
                      <p className="text-muted-foreground text-sm">No events in last 24h</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Event Log */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Event Log</CardTitle>
              <Button variant="outline" size="sm" onClick={fetchEvents}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {events.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No events recorded</p>
                  ) : (
                    events.map((event) => (
                      <div key={event.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{event.source}</Badge>
                          <ArrowRight className="h-3 w-3 text-slate-400" />
                          <div>
                            <p className="font-medium text-sm">{event.event_type}</p>
                            <p className="text-xs text-muted-foreground">
                              {event.entity_type} ({event.entity_id?.slice(0, 8)}...) • {event.action}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">{formatDate(event.created_at)}</p>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Quality Tab */}
        <TabsContent value="quality" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Quality Rules */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Quality Rules</CardTitle>
                  <CardDescription>Define data validation rules</CardDescription>
                </div>
                <Button size="sm" onClick={() => setRuleModalOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Rule
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {qualityRules.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No rules defined</p>
                    ) : (
                      qualityRules.map((rule) => (
                        <div key={rule.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{rule.name}</p>
                              <Badge variant={rule.severity === 'error' ? 'destructive' : 'secondary'} className="text-xs">
                                {rule.severity}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {rule.entity_type}.{rule.field_name} • {rule.rule_type}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => deleteQualityRule(rule.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Quality Issues */}
            <Card>
              <CardHeader>
                <CardTitle>Open Issues</CardTitle>
                <CardDescription>Data quality issues to resolve</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {qualityIssues.length === 0 ? (
                      <div className="text-center py-8">
                        <CheckCircle2 className="h-12 w-12 mx-auto text-green-200 mb-2" />
                        <p className="text-muted-foreground">No open issues</p>
                      </div>
                    ) : (
                      qualityIssues.map((issue) => (
                        <div key={issue.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge variant={issue.severity === 'error' ? 'destructive' : 'secondary'} className="text-xs">
                                {issue.severity}
                              </Badge>
                              <p className="font-medium text-sm">{issue.entity_type}</p>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {issue.field_name}: "{issue.current_value}" is invalid
                            </p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => resolveIssue(issue.id)}>
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Data Models Tab */}
        <TabsContent value="models" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Data Models</CardTitle>
              <CardDescription>View and manage data entity schemas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {dataModels.map((model) => (
                  <Card key={model.id} className={`cursor-pointer transition-colors ${selectedModel?.id === model.id ? 'border-blue-500' : ''}`}
                    onClick={() => setSelectedModel(model)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{model.name}</CardTitle>
                        {model.is_system && <Badge variant="secondary" className="text-xs">System</Badge>}
                      </div>
                      <CardDescription className="text-xs">{model.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        {model.fields?.length || 0} fields • v{model.version}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {selectedModel && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="text-base">{selectedModel.name} Schema</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Fields</Label>
                        <div className="mt-2 space-y-2">
                          {selectedModel.fields?.map((field, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                              <div className="flex items-center gap-2">
                                <code className="text-sm">{field.name}</code>
                                <Badge variant="outline" className="text-xs">{field.type}</Badge>
                              </div>
                              {field.required && <Badge className="text-xs">Required</Badge>}
                            </div>
                          ))}
                        </div>
                      </div>

                      {selectedModel.relationships?.length > 0 && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Relationships</Label>
                          <div className="mt-2 space-y-2">
                            {selectedModel.relationships.map((rel, idx) => (
                              <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
                                <span>{rel.type}</span>
                                <ArrowRight className="h-3 w-3" />
                                <code>{rel.target_entity}</code>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Studio Tab */}
        <TabsContent value="studio" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Query Builder */}
            <Card className="md:col-span-1">
              <CardHeader>
                <CardTitle className="text-base">Query Builder</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Collection</Label>
                  <Select value={queryConfig.collection} onValueChange={(v) => setQueryConfig({...queryConfig, collection: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {collections.map((col) => (
                        <SelectItem key={col.name} value={col.name}>{col.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Limit</Label>
                  <Input 
                    type="number"
                    value={queryConfig.limit}
                    onChange={(e) => setQueryConfig({...queryConfig, limit: parseInt(e.target.value) || 100})}
                    max={1000}
                  />
                </div>

                <Button className="w-full" onClick={runQuery} disabled={loading}>
                  <Play className="h-4 w-4 mr-1" />
                  Run Query
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            <Card className="md:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Results</CardTitle>
                  {queryResults && (
                    <CardDescription>
                      Showing {queryResults.count} of {queryResults.total} records
                    </CardDescription>
                  )}
                </div>
                {queryResults && (
                  <Button variant="outline" size="sm" onClick={exportResults}>
                    <Download className="h-4 w-4 mr-1" />
                    Export JSON
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  {!queryResults ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Search className="h-12 w-12 mx-auto text-slate-200 mb-3" />
                      <p>Run a query to see results</p>
                    </div>
                  ) : queryResults.results?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No results found</p>
                  ) : (
                    <pre className="text-xs bg-slate-50 p-4 rounded-lg overflow-auto">
                      {JSON.stringify(queryResults.results, null, 2)}
                    </pre>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Webhook Modal */}
      <Dialog open={webhookModalOpen} onOpenChange={setWebhookModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Webhook</DialogTitle>
            <DialogDescription>Configure a webhook to send data to external services</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="My Webhook"
                value={webhookForm.name}
                onChange={(e) => setWebhookForm({...webhookForm, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Endpoint URL</Label>
              <Input
                placeholder="https://api.example.com/webhook"
                value={webhookForm.endpoint_url}
                onChange={(e) => setWebhookForm({...webhookForm, endpoint_url: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Optional description..."
                value={webhookForm.description}
                onChange={(e) => setWebhookForm({...webhookForm, description: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Events to Listen</Label>
              <ScrollArea className="h-[200px] border rounded-lg p-3">
                {Object.entries(webhookEvents).map(([source, events]) => (
                  <div key={source} className="mb-3">
                    <p className="font-medium text-sm mb-2 capitalize">{source}</p>
                    <div className="flex flex-wrap gap-2">
                      {events.map((event) => (
                        <Badge
                          key={event}
                          variant={webhookForm.events.includes(event) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleEventSelection(event)}
                        >
                          {event}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWebhookModalOpen(false)}>Cancel</Button>
            <Button onClick={createWebhook} disabled={loading}>Create Webhook</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Webhook Logs Modal */}
      <Dialog open={logsModalOpen} onOpenChange={setLogsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Webhook Logs: {selectedWebhook?.name}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {webhookLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No logs yet</p>
              ) : (
                webhookLogs.map((log) => (
                  <div key={log.id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {log.success ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                        <Badge variant="outline">{log.event_type}</Badge>
                        <Badge variant={log.success ? 'secondary' : 'destructive'}>
                          {log.response_status || 'Error'}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatDate(log.created_at)}</span>
                    </div>
                    {log.error_message && (
                      <p className="text-sm text-red-600">{log.error_message}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogsModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Quality Rule Modal */}
      <Dialog open={ruleModalOpen} onOpenChange={setRuleModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Quality Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Rule Name</Label>
              <Input
                placeholder="Email Required"
                value={ruleForm.name}
                onChange={(e) => setRuleForm({...ruleForm, name: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Entity Type</Label>
                <Select value={ruleForm.entity_type} onValueChange={(v) => setRuleForm({...ruleForm, entity_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contact">Contact</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="deal">Deal</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Field Name</Label>
                <Input
                  placeholder="email"
                  value={ruleForm.field_name}
                  onChange={(e) => setRuleForm({...ruleForm, field_name: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rule Type</Label>
                <Select value={ruleForm.rule_type} onValueChange={(v) => setRuleForm({...ruleForm, rule_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="required">Required</SelectItem>
                    <SelectItem value="format">Format</SelectItem>
                    <SelectItem value="range">Range</SelectItem>
                    <SelectItem value="unique">Unique</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Severity</Label>
                <Select value={ruleForm.severity} onValueChange={(v) => setRuleForm({...ruleForm, severity: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="error">Error</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleModalOpen(false)}>Cancel</Button>
            <Button onClick={createQualityRule} disabled={loading}>Create Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataHubPage;
